import { defineStore } from 'pinia';
import type { User } from '@/types';
import API from '@/api';
import { setCurrentUser } from '@/state/authState';

// Concurrent callers during router-guard resolution would otherwise each fire
// their own /user/current request.
let loadCurrentUserPromise: Promise<void> | null = null;

export const useCurrentUserStore = defineStore('currentUser', {
    state: (): { user: User | null } => ({
        user: null,
    }),
    getters: {
        currentUser: state => state.user,
        isSubscribed: (state): boolean => {
            if (state.user == null) return false;
            return state.user.subscriptions.some(subscription => subscription.ends_at == null);
        },
    },
    actions: {
        async loadCurrentUser(): Promise<void> {
            if (this.user != null) return;
            if (loadCurrentUserPromise != null) return loadCurrentUserPromise;

            loadCurrentUserPromise = this.refreshCurrentUser()
                .finally(() => {
                    loadCurrentUserPromise = null;
                });

            return loadCurrentUserPromise;
        },
        async refreshCurrentUser(): Promise<void> {
            const user = await API.user.fetchCurrentUser();
            this.user = user;
            setCurrentUser(user);
        },
        clearCurrentUser(): void {
            this.user = null;
            setCurrentUser(null);
        },
    },
});
